import { Box } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { IconButton } from '@osmosis/react-ui/components/IconButton';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { FormattedMessage } from 'react-intl';
import { H2 } from '@osmosis/react-ui/components/Text';
import { H3 } from '@osmosis/react-ui/components/Text';
import { H4 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import { SPACED_REPETITION_LINK } from '../../../../../constants';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { lighten } from '@osmosis/shared/styles/colorModifiers';
import * as motion from 'motion/react-m';
import { expand } from '../../../../../styles/animation';

type Props = {
  hidePopup: () => void;
};

const containerStyle = (bgColor: string) => ({
  position: 'absolute',
  maxWidth: '550px',
  width: '100%',
  backgroundColor: bgColor,
  borderRadius: '5px',
  left: '45%',
  transform: 'translateX(-45%)',
  top: '75%',
  boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
  textAlign: 'center',
});

const arrowStyle = (bgColor: string) => ({
  position: 'absolute',
  bottom: '100%',
  left: '50%',
  borderBottom: `10px solid ${bgColor}`,
  borderLeft: '10px solid transparent',
  borderRight: '10px solid transparent',
});

const confidenceBlockStyle = { flex: 1, p: 2 };

/**
 * RateConfidenceWindow.
 */
export const RateConfidenceWindow = (props: Props) => {
  const { hidePopup } = props;

  const colors = useColors();
  const bgColor = lighten(0.5, colors.primary);

  return (
    <motion.div {...expand()} transition={{ delay: 0.5 }}>
      <Box sx={containerStyle(bgColor)}>
        <Box sx={arrowStyle(bgColor)} />
        <Box>
          <Box sx={{ position: 'absolute', right: 0, mr: 2, mt: 1 }}>
            <IconButton
              type="button"
              variant="transparent"
              color="text"
              iconSize={14}
              onClick={hidePopup}
              icon={<Icon icon={faTimes} />}
              data-testid="confidence-modal.button.close"
            />
          </Box>
          <Box sx={{ p: 3 }}>
            <H2>
              <FormattedMessage
                defaultMessage="Try rating your confidence to continue"
                id="confidence-modal.text.header"
              />
            </H2>
          </Box>
          <Box sx={{ m: '0 10px 20px 10px' }}>
            <P>
              <FormattedMessage
                defaultMessage="Rating your confidence helps Osmosis determine when this flashcard will show up in your spaced repetition queue."
                id="confidence-modal.text.body"
              />
            </P>
          </Box>
          <Box
            sx={{
              display: 'flex',
              backgroundColor: 'primaryLight',
            }}
          >
            <Box sx={confidenceBlockStyle}>
              <H3 color="primaryText">
                <FormattedMessage
                  defaultMessage="Low"
                  id="confidence-modal.text.low-title"
                />
              </H3>
              <P>
                <FormattedMessage
                  defaultMessage="You have no idea."
                  id="confidence-modal.text.low-description"
                />
              </P>
            </Box>
            <Box sx={confidenceBlockStyle}>
              <H3 color="primaryText">
                <FormattedMessage defaultMessage="Medium" id="common.medium" />
              </H3>
              <P>
                <FormattedMessage
                  defaultMessage="You might know it."
                  id="confidence-modal.text.medium-description"
                />
              </P>
            </Box>
            <Box sx={confidenceBlockStyle}>
              <H3 color="primaryText">
                <FormattedMessage defaultMessage="High" id="common.high" />
              </H3>
              <P>
                <FormattedMessage
                  defaultMessage="You definitely know it."
                  id="confidence-modal.text.high-description"
                />
              </P>
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', height: '50px', justifyContent: 'center' }}
          >
            <H4
              data-testid="confidence-modal.link.spaced-repetition"
              as="a"
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                alignSelf: 'center',
              }}
              color="primary"
              href={SPACED_REPETITION_LINK}
              target="_blank"
            >
              <FormattedMessage
                id="confidence-modal.text.spaced-repetition"
                defaultMessage="What's spaced repetition?"
              />
            </H4>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};
